import React, { useEffect, useCallback, useState } from 'react';
import TableComponent from '@components/shared/TableComponent';
import { ColumnDef } from '@tanstack/react-table';
import { Plus, CheckCircle } from 'lucide-react';
import { Integration } from '../../../types';
import { useIntegrations } from '../../../hooks/useIntegrations';
import { format } from 'date-fns';
import { useNotifications } from '../../../contexts/NotificationContext';
import NewIntegrationDialog from '../../../components/dialogs/NewIntegrationDialog';


const IntegrationsTab: React.FC = () => {
  const { showNotification } = useNotifications();
  const [showNewIntegrationDialog, setShowNewIntegrationDialog] = useState(false);
  const [editingIntegration, setEditingIntegration] = useState<Integration | null>(null);

  const [page, setPage] = useState(0);
  const [pageSize] = useState(10); // Define your page size
  const {
    integrations,
    loadIntegrations,
    isLoading, // Assuming useIntegrations provides isLoading
    hasMore, // Assuming useIntegrations provides hasMore
  } = useIntegrations();

  const refreshIntegrations = useCallback(() => {
    setPage(0); // Reset page to 0 on refresh
    loadIntegrations({ page: 0, pageSize }).catch(() => {
      showNotification('error', 'Failed to load integrations');
    });
  }, [loadIntegrations, pageSize, showNotification]);

  const loadMoreIntegrations = useCallback(() => {
    if (!isLoading && hasMore) {
      const nextPage = page + 1;
      loadIntegrations({ page: nextPage, pageSize, append: true }).then(() => { // Assuming 'append' option to add to existing data
        setPage(nextPage);
      }).catch(() => {
        showNotification('error', 'Failed to load more integrations');
      });
    }
  }, [isLoading, hasMore, page, pageSize, loadIntegrations, showNotification]);

  useEffect(() => {
    refreshIntegrations();
  }, [refreshIntegrations]);

  const handleIntegrationRowClick = (row: Integration) => {
    setEditingIntegration(row);
    setShowNewIntegrationDialog(true);
  };

  const handleCloseDialog = () => {
    setShowNewIntegrationDialog(false);
    setEditingIntegration(null);
    refreshIntegrations(); // Refresh data after dialog closes
  };

  const integrationColumns: ColumnDef<Integration>[] = [
    {
      accessorKey: 'name',
      header: 'Name',
      cell: info => (
        <div className="flex items-center">
          <img src={info.row.original.logo_url} alt="avatar" className="h-6 w-6 rounded-full mr-2" />
          <span className="font-medium text-gray-900 dark:text-white">{info.getValue() as string}</span>
        </div>
      ),
      size: 200, // Wide
    },
    {
      accessorKey: 'category',
      header: 'Category',
      cell: info => info.getValue(),
      size: 150, // Wide
    },
    {
      accessorKey: 'destination',
      header: 'Data destination',
      cell: info => (
        <div className="flex justify-center">
          {info.getValue() ? <CheckCircle className="h-5 w-5 text-green-500" /> : null}
        </div>
      ),
      size: 50, // Narrow
    },
    {
      accessorKey: 'sync_injector',
      header: 'Sync Injector support',
      cell: info => (
        <div className="flex justify-center">
          {info.getValue() ? <CheckCircle className="h-5 w-5 text-green-500" /> : null}
        </div>
      ),
      size: 50, // Narrow
    },
    {
      accessorKey: 'updated_at',
      header: 'Date modified',
      cell: info => info.getValue() ? format(new Date(info.getValue() as string), 'yyyy-MM-dd') : 'N/A',
      size: 100, // Narrow
    },
  ];

  return (
    <div className="space-y-8 flex flex-col h-full"> {/* Added flex flex-col h-full */}
      <TableComponent
        title="Integrations"
        data={integrations}
        columns={integrationColumns}
        onRowClick={handleIntegrationRowClick}
        isLoading={isLoading}
        paginationType="lazy" // Set pagination type to lazy
        hasMore={hasMore}
        onLoadMore={loadMoreIntegrations}
        buttons={
          <button
            type="button"
            className="inline-flex items-center px-4 py-2 border border-transparent shadow-sm text-sm font-medium rounded-md text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 dark:bg-blue-700 dark:hover:bg-blue-800 transition-colors duration-200"
            onClick={() => {
              setEditingIntegration(null);
              setShowNewIntegrationDialog(true);
            }}
          >
            <Plus className="-ml-1 mr-2 h-5 w-5" aria-hidden="true" />
            Add Integration
          </button>
        }
      />
      {showNewIntegrationDialog && (
        <NewIntegrationDialog
          onClose={handleCloseDialog}
          isEdit={!!editingIntegration}
          initialData={editingIntegration}
          onIntegrationCreated={refreshIntegrations}
        />
      )}
    </div>
  );
};

export default IntegrationsTab;
